/**
 * @deprecated LEGACY ONLY — Fase D2 refactor `feature/refactor-logicas-comunicacion` (2026-06).
 * Este archivo se mantiene temporalmente solo por compatibilidad de imports embebidos.
 * No usar para nuevas implementaciones; flujo principal de comunicación vive en CommLogicsPage v2.
 * Ver /app/docs/refactor_comm_logics_audit.md
 */
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Zap, Trash2, ArrowRight, Sparkles, Loader2, MousePointerClick, Play } from 'lucide-react';
import HelpTip from '@/components/HelpTip';

const TRIGGERS = [
  { value: 'stage_change', label: 'La etapa cambia', icon: '>', color: 'from-blue-500/20 to-blue-600/10', dot: 'bg-blue-400' },
  { value: 'new_opportunity', label: 'Se crea nueva oportunidad', icon: '+', color: 'from-emerald-500/20 to-emerald-600/10', dot: 'bg-emerald-400' },
  { value: 'value_threshold', label: 'El valor supera un monto', icon: '$', color: 'from-amber-500/20 to-amber-600/10', dot: 'bg-amber-400' },
  { value: 'priority_change', label: 'La prioridad cambia', icon: '!', color: 'from-pink-500/20 to-pink-600/10', dot: 'bg-pink-400' },
];
const ACTIONS = [
  { value: 'change_stage', label: 'Cambiar etapa a', color: 'from-cyan-500/20 to-cyan-600/10', dot: 'bg-cyan-400' },
  { value: 'assign_to', label: 'Asignar a', color: 'from-violet-500/20 to-violet-600/10', dot: 'bg-violet-400' },
  { value: 'change_priority', label: 'Cambiar prioridad a', color: 'from-orange-500/20 to-orange-600/10', dot: 'bg-orange-400' },
  { value: 'send_notification', label: 'Enviar notificacion', color: 'from-teal-500/20 to-teal-600/10', dot: 'bg-teal-400' },
  { value: 'send_email', label: 'Enviar email', color: 'from-indigo-500/20 to-indigo-600/10', dot: 'bg-indigo-400' },
  { value: 'send_whatsapp', label: 'Enviar WhatsApp', color: 'from-emerald-500/20 to-emerald-600/10', dot: 'bg-emerald-400' },
  { value: 'add_tag', label: 'Agregar etiqueta', color: 'from-rose-500/20 to-rose-600/10', dot: 'bg-rose-400' },
];
const PRIORITIES_OPTS = ['high', 'medium', 'low'];

export default function RulesPage() {
  const [rules, setRules] = useState([]);
  const [stages, setStages] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [form, setForm] = useState({ name: '', trigger_type: 'stage_change', trigger_config: {}, action_type: 'send_notification', action_config: {}, is_active: true });

  const load = useCallback(() => {
    Promise.all([api.get('/rules'), api.get('/pipeline'), api.get('/team')])
      .then(([r, p, t]) => { setRules(r.data); setStages(p.data); setTeam(t.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const seedDefaults = async () => {
    if (!window.confirm('Sembrar 10 reglas default recomendadas? No se duplicaran las existentes.')) return;
    setSeeding(true);
    try {
      const r = await api.post('/rules/seed-defaults');
      toast.success(`${r.data.total_created} reglas creadas (${r.data.skipped.length} omitidas)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al sembrar reglas'); }
    finally { setSeeding(false); }
  };

  const getStageName = (id) => stages.find(s => s.id === id)?.name || id;
  const getVendorName = (id) => team.find(m => m.id === id)?.name || id;
  const getTriggerMeta = (t) => TRIGGERS.find(tr => tr.value === t) || { label: t, color: 'from-white/10 to-white/5', dot: 'bg-white' };
  const getActionMeta = (a) => ACTIONS.find(ac => ac.value === a) || { label: a, color: 'from-white/10 to-white/5', dot: 'bg-white' };

  const save = async () => {
    try {
      await api.post('/rules', form);
      toast.success('Regla creada');
      setDialogOpen(false);
      setForm({ name: '', trigger_type: 'stage_change', trigger_config: {}, action_type: 'send_notification', action_config: {}, is_active: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleRule = async (rule) => {
    await api.put(`/rules/${rule.id}`, { is_active: !rule.is_active });
    toast.success(rule.is_active ? 'Regla desactivada' : 'Regla activada');
    load();
  };

  const removeRule = async (id) => {
    if (!window.confirm('Eliminar regla?')) return;
    await api.delete(`/rules/${id}`);
    toast.success('Regla eliminada');
    load();
  };

  const describeTrigger = (rule) => {
    const meta = getTriggerMeta(rule.trigger_type);
    let label = meta.label;
    const tc = rule.trigger_config || {};
    if (rule.trigger_type === 'stage_change' && tc.to_stage) label += ` a "${getStageName(tc.to_stage)}"`;
    if (rule.trigger_type === 'value_threshold' && tc.threshold) label += ` de $${Number(tc.threshold).toLocaleString('es-MX')}`;
    if (rule.trigger_type === 'priority_change' && tc.to_priority) label += ` a ${tc.to_priority}`;
    return { ...meta, label };
  };

  const describeAction = (rule) => {
    const meta = getActionMeta(rule.action_type);
    let label = meta.label;
    const ac = rule.action_config || {};
    if (rule.action_type === 'change_stage' && ac.target_stage) label += ` "${getStageName(ac.target_stage)}"`;
    if (rule.action_type === 'assign_to' && ac.target_user) label += ` ${getVendorName(ac.target_user)}`;
    if (rule.action_type === 'change_priority' && ac.target_priority) label += ` ${ac.target_priority}`;
    if (rule.action_type === 'send_notification' && ac.message) label += `: "${ac.message.slice(0, 40)}${ac.message.length > 40 ? '...' : ''}"`;
    if (rule.action_type === 'send_email' && ac.subject) label += `: "${ac.subject}"`;
    if (rule.action_type === 'send_whatsapp' && ac.message) label += `: "${ac.message.slice(0, 40)}..."`;
    if (rule.action_type === 'add_tag' && ac.tag) label += ` "${ac.tag}"`;
    return { ...meta, label };
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="rules-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white inline-flex items-center gap-2">Reglas de Automatizacion <HelpTip title="¿Qué son las reglas?" content="Una regla hace algo AUTOMÁTICAMENTE cuando pasa algo en una oportunidad. Ejemplo: cuando llega a Ganado, manda un WhatsApp. Tú eliges el disparador (cuándo) y la acción (qué hacer)." /></h2>
          <p className="text-muted-foreground text-sm mt-1">Canvas tipo Monday — conecta disparadores (cuando) con acciones (entonces)</p>
        </div>
        <div className="flex gap-2">
          {rules.length === 0 && (
            <Button onClick={seedDefaults} disabled={seeding} variant="outline" className="border-primary/30 text-primary hover:bg-primary/10" data-testid="seed-rules-btn">
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Sembrar 10 reglas default
            </Button>
          )}
          <Button onClick={() => setDialogOpen(true)} className="bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 font-semibold" data-testid="add-rule-btn">
            <Plus className="w-4 h-4 mr-2" />Nueva Regla
          </Button>
        </div>
      </div>

      {rules.length === 0 ? (
        <Card className="bg-card/50 backdrop-blur-md border-white/5">
          <CardContent className="py-16 text-center">
            <Zap className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-white font-semibold mb-2">Sin reglas activas</p>
            <p className="text-muted-foreground text-sm mb-6">Siembra 10 reglas recomendadas o crea tu primera desde cero.</p>
            <Button onClick={seedDefaults} disabled={seeding} className="bg-primary hover:bg-primary/90 text-black font-bold" data-testid="seed-rules-empty-btn">
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Sembrar reglas recomendadas
            </Button>
          </CardContent>
        </Card>
      ) : (
        // Canvas tipo Monday: cada regla es una "conexion" visual When -> Arrow -> Then
        <div className="space-y-3" data-testid="rules-canvas">
          {rules.map(rule => {
            const t = describeTrigger(rule);
            const a = describeAction(rule);
            return (
              <div
                key={rule.id}
                className={`relative rounded-2xl overflow-hidden ${rule.is_active ? 'ring-1 ring-white/5' : 'opacity-50'}`}
                data-testid={`rule-card-${rule.id}`}
              >
                {/* Gradient background */}
                <div className={`absolute inset-0 bg-gradient-to-r ${t.color} via-transparent ${a.color.replace('from-', 'to-')}`} style={{ zIndex: 0 }} />
                <div className="relative z-10 p-4 bg-[#1F2833]/60 backdrop-blur-sm border border-white/5 rounded-2xl">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-4 flex-1 min-w-[280px]">
                      {/* Trigger node */}
                      <div className="flex-1 min-w-[200px] p-3 rounded-xl bg-black/30 border border-white/5">
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-2 h-2 rounded-full ${t.dot}`} />
                          <p className="text-[9px] font-bold uppercase tracking-widest text-white/40">Cuando</p>
                        </div>
                        <p className="text-xs text-white font-medium">{t.label}</p>
                      </div>

                      {/* Arrow */}
                      <div className="hidden sm:flex items-center justify-center shrink-0">
                        <div className="flex items-center gap-1">
                          <div className="h-px w-4 bg-gradient-to-r from-white/20 to-white/5" />
                          <ArrowRight className="w-4 h-4 text-white/40" />
                          <div className="h-px w-4 bg-gradient-to-r from-white/5 to-white/20" />
                        </div>
                      </div>

                      {/* Action node */}
                      <div className="flex-1 min-w-[200px] p-3 rounded-xl bg-black/30 border border-white/5">
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-2 h-2 rounded-full ${a.dot}`} />
                          <p className="text-[9px] font-bold uppercase tracking-widest text-white/40">Entonces</p>
                        </div>
                        <p className="text-xs text-white font-medium">{a.label}</p>
                      </div>
                    </div>

                    {/* Meta + actions */}
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right">
                        <p className="text-xs font-semibold text-white">{rule.name}</p>
                        {rule.is_default && <Badge variant="outline" className="text-[9px] mt-1 border-primary/30 text-primary">Default</Badge>}
                      </div>
                      <Switch checked={rule.is_active} onCheckedChange={() => toggleRule(rule)} data-testid={`rule-toggle-${rule.id}`} />
                      <Button variant="ghost" size="sm" onClick={() => removeRule(rule.id)} className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10" data-testid={`rule-delete-${rule.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Rule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-[#1F2833] border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2"><Zap className="w-5 h-5 text-violet-400" />Nueva Regla</DialogTitle>
            <DialogDescription className="text-muted-foreground">Configura una automatizacion para tu pipeline</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="space-y-2"><Label>Nombre de la regla</Label><Input data-testid="rule-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Notificar al cerrar negocio" className="bg-black/20 border-white/10 focus:border-primary" /></div>

            {/* Trigger */}
            <div className="p-4 rounded-xl bg-primary/5 border border-primary/10 space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-1.5">Cuando... <HelpTip title="Disparador (Cuándo)" content="Qué tiene que pasar para que la regla se active: cambia de etapa, se crea una oportunidad, el valor supera un monto, o cambia la prioridad." /></p>
              <Select value={form.trigger_type} onValueChange={v => setForm({...form, trigger_type: v, trigger_config: {}})}>
                <SelectTrigger className="bg-black/20 border-white/10" data-testid="rule-trigger-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TRIGGERS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              {form.trigger_type === 'stage_change' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">De etapa (opcional)</Label>
                    <Select value={form.trigger_config.from_stage || ''} onValueChange={v => setForm({...form, trigger_config: {...form.trigger_config, from_stage: v}})}>
                      <SelectTrigger className="bg-black/20 border-white/10" data-testid="rule-trigger-from"><SelectValue placeholder="Cualquiera" /></SelectTrigger>
                      <SelectContent><SelectItem value="any">Cualquiera</SelectItem>{stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">A etapa</Label>
                    <Select value={form.trigger_config.to_stage || ''} onValueChange={v => setForm({...form, trigger_config: {...form.trigger_config, to_stage: v}})}>
                      <SelectTrigger className="bg-black/20 border-white/10" data-testid="rule-trigger-to"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {form.trigger_type === 'value_threshold' && (
                <div className="space-y-1">
                  <Label className="text-xs">Monto umbral</Label>
                  <Input data-testid="rule-trigger-threshold" type="number" value={form.trigger_config.threshold || ''} onChange={e => setForm({...form, trigger_config: {...form.trigger_config, threshold: e.target.value}})} className="bg-black/20 border-white/10 focus:border-primary" placeholder="1000000" />
                </div>
              )}
            </div>

            {/* Action */}
            <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/10 space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-emerald-400 flex items-center gap-1.5">Entonces... <HelpTip title="Acción (Entonces)" content="Qué hace la regla cuando se dispara: mover de etapa, asignar a alguien, cambiar prioridad, mandar notificación/email/WhatsApp, o agregar una etiqueta." /></p>
              <Select value={form.action_type} onValueChange={v => setForm({...form, action_type: v, action_config: {}})}>
                <SelectTrigger className="bg-black/20 border-white/10" data-testid="rule-action-type"><SelectValue /></SelectTrigger>
                <SelectContent>{ACTIONS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
              {form.action_type === 'change_stage' && (
                <Select value={form.action_config.target_stage || ''} onValueChange={v => setForm({...form, action_config: {...form.action_config, target_stage: v}})}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="rule-action-stage"><SelectValue placeholder="Etapa destino" /></SelectTrigger>
                  <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {form.action_type === 'assign_to' && (
                <Select value={form.action_config.target_user || ''} onValueChange={v => setForm({...form, action_config: {...form.action_config, target_user: v}})}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="rule-action-user"><SelectValue placeholder="Asignar a" /></SelectTrigger>
                  <SelectContent>{team.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {form.action_type === 'change_priority' && (
                <Select value={form.action_config.target_priority || ''} onValueChange={v => setForm({...form, action_config: {...form.action_config, target_priority: v}})}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="rule-action-priority"><SelectValue placeholder="Prioridad" /></SelectTrigger>
                  <SelectContent>{PRIORITIES_OPTS.map(p => <SelectItem key={p} value={p}>{p === 'high' ? 'Alta' : p === 'medium' ? 'Media' : 'Baja'}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {form.action_type === 'send_notification' && (
                <Input data-testid="rule-action-message" value={form.action_config.message || ''} onChange={e => setForm({...form, action_config: {...form.action_config, message: e.target.value}})} placeholder="Mensaje de notificacion..." className="bg-black/20 border-white/10 focus:border-primary" />
              )}
              {form.action_type === 'send_email' && (
                <div className="space-y-2">
                  <Input data-testid="rule-action-email-subject" value={form.action_config.subject || ''} onChange={e => setForm({...form, action_config: {...form.action_config, subject: e.target.value}})} placeholder="Asunto del email" className="bg-black/20 border-white/10 focus:border-primary" />
                  <Input data-testid="rule-action-email-body" value={form.action_config.message || ''} onChange={e => setForm({...form, action_config: {...form.action_config, message: e.target.value}})} placeholder="Cuerpo — usa {{lead_name}} {{property_title}}" className="bg-black/20 border-white/10 focus:border-primary" />
                </div>
              )}
              {form.action_type === 'send_whatsapp' && (
                <Input data-testid="rule-action-whatsapp" value={form.action_config.message || ''} onChange={e => setForm({...form, action_config: {...form.action_config, message: e.target.value}})} placeholder="Mensaje WhatsApp — {{lead_name}} para personalizar" className="bg-black/20 border-white/10 focus:border-primary" />
              )}
              {form.action_type === 'add_tag' && (
                <Input data-testid="rule-action-tag" value={form.action_config.tag || ''} onChange={e => setForm({...form, action_config: {...form.action_config, tag: e.target.value}})} placeholder="Etiqueta (ej. caliente, seguimiento)" className="bg-black/20 border-white/10 focus:border-primary" />
              )}
            </div>

            <Button onClick={save} className="w-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 font-semibold" data-testid="save-rule-btn">Crear Regla</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
